import { useCallback, useEffect, useState } from "react";

import {
  FinalDoctorRecommendationFields,
  FinalRecommendationKey,
} from "../types/finalDoctorRecommendation";

import { FinalDoctorRecommendationService } from "../services/finalDoctorRecommendation.service";

const EMPTY: FinalDoctorRecommendationFields = {
  parentSummary: "",
  generalWellnessAndLifestyle: "",
  growthAndVitals: "",
  dietAndNutrition: "",
  sleepHealth: "",
  sensoryProfile: "",
  developmentalMilestones: "",
  seizuresAndEpilepsy: "",
  allergiesAndAtopy: "",
  skinAndDermatology: "",
  hearing: "",
  vision: "",
  dentalHealth: "",
  bloodInvestigations: "",
  parentNextSteps: "",
  reviewingPaediatricianOverallImpressions: "",
  summaryTab1: "", // <--- Added
  summaryTab2: "", // <--- Added
  summaryTab3: "", // <--- Added
};

export function useFinalDoctorRecommendation(
  emrId: string,
  doctorName?: string
) {
  const [loading, setLoading] = useState(true);

  const [recommendation, setRecommendation] =
    useState<FinalDoctorRecommendationFields>(EMPTY);

  const [documentId, setDocumentId] = useState("");

  const load = useCallback(async () => {
    if (!emrId) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const doc =
        await FinalDoctorRecommendationService.get(emrId);

      setDocumentId(doc._id);

      setRecommendation({
        ...EMPTY,
        ...(doc.recommendation || {}),
      });
    } catch (err: any) {
      // If not found, create a new recommendation
      if (
        err?.response?.status === 404 &&
        doctorName
      ) {
        try {
          const doc =
            await FinalDoctorRecommendationService.create(
              emrId,
              doctorName
            );

          setDocumentId(doc._id);

          setRecommendation({
            ...EMPTY,
            ...(doc.recommendation || {}),
          });
        } catch (createError) {
          console.error(createError);
        }
      } else {
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }, [emrId, doctorName]);

  useEffect(() => {
    load();
  }, [load]);

  const saveField = async (
    field: FinalRecommendationKey,
    value: string
  ) => {
    if (!doctorName) return;

    setRecommendation((prev) => ({
      ...prev,
      [field]: value,
    }));

    try {
      const updated =
        await FinalDoctorRecommendationService.updateField(
          emrId,
          doctorName,
          field,
          value
        );

      setRecommendation({
        ...EMPTY,
        ...(updated.recommendation || {}),
      });

      setDocumentId(updated._id);
    } catch (err) {
      console.error(err);
    }
  };

  const saveAll = async (
    payload: Partial<FinalDoctorRecommendationFields>
  ) => {
    if (!doctorName) return;

    setRecommendation((prev) => ({
      ...prev,
      ...payload,
    }));

    try {
      const updated =
        await FinalDoctorRecommendationService.updateAll(
          emrId,
          doctorName,
          payload
        );

      setRecommendation({
        ...EMPTY,
        ...(updated.recommendation || {}),
      });

      setDocumentId(updated._id);
    } catch (err) {
      console.error(err);
    }
  };

  return {
    loading,
    recommendation,
    saveField,
    saveAll,
    documentId,
    reload: load,
  };
}